import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import {
  dismissAnnouncement,
  getActiveAnnouncement,
} from '../services/api/announcement.service';
import type { Announcement, AnnouncementAction } from '../types/announcement';

interface AnnouncementContextValue {
  announcement: Announcement | null;
  isOpen: boolean;
  loading: boolean;
  closeAnnouncement: (action?: AnnouncementAction) => Promise<void>;
}

const AnnouncementContext = createContext<AnnouncementContextValue | undefined>(undefined);

export const AnnouncementProvider: React.FC<{
  isAuthenticated: boolean;
  children: React.ReactNode;
}> = ({ isAuthenticated, children }) => {
  const [announcement, setAnnouncement] = useState<Announcement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    if (!isAuthenticated) {
      setAnnouncement(null);
      setIsOpen(false);
      return () => { cancelled = true; };
    }

    setLoading(true);
    getActiveAnnouncement()
      .then((active) => {
        if (cancelled) return;
        setAnnouncement(active);
        setIsOpen(!!active);
      })
      .catch(() => {
        // Announcements are optional and must never prevent the app from loading.
        if (!cancelled) {
          setAnnouncement(null);
          setIsOpen(false);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [isAuthenticated]);

  const closeAnnouncement = useCallback(async (action: AnnouncementAction = 'dismissed') => {
    const current = announcement;
    setIsOpen(false);
    setAnnouncement(null);
    if (!current) return;

    try {
      await dismissAnnouncement(current.id, action);
    } catch (error) {
      // The popup stays closed for this page view. If persistence failed, it can
      // safely reappear after the next login rather than blocking the user now.
      console.error('[Announcements] Failed to persist dismissal:', error);
    }
  }, [announcement]);

  return (
    <AnnouncementContext.Provider value={{ announcement, isOpen, loading, closeAnnouncement }}>
      {children}
    </AnnouncementContext.Provider>
  );
};

export const useAnnouncement = (): AnnouncementContextValue => {
  const context = useContext(AnnouncementContext);
  if (!context) throw new Error('useAnnouncement must be used within AnnouncementProvider');
  return context;
};
